<template>
  <div class="chart-section">
    <h3 class="section-title">Patient Growth Over Time</h3>
    <div class="chart-card">
      <div class="chart-container">
        <canvas ref="chartCanvas"></canvas>
        <div ref="tooltipEl" class="custom-tooltip"></div>
        <div v-if="!hasData" class="chart-empty">No data available for the selected time range.</div>
      </div>
      <div class="chart-legend">
        <div class="legend-dot"></div>
        <p class="legend-label">Total Patients (Practice View)</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { Chart, registerables } from 'chart.js';
import { processPatientGrowthData, CHART_COLORS } from '../utils/chartHelpers';

Chart.register(...registerables);

const store = useStore();
const chartCanvas = ref(null);
const tooltipEl = ref(null);
let chartInstance = null;

const series = computed(() => store.getters['myPractice/clinicDashboard/patientGrowthChartData']);
const hasData = computed(() => series.value && series.value.length > 0);

// Custom external tooltip handler
const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const tooltipElement = tooltipEl.value;
  
  if (!tooltipElement) return;

  // Hide if no tooltip
  if (tooltip.opacity === 0) {
    tooltipElement.style.opacity = '0';
    return;
  }

  // Set content
  if (tooltip.body) {
    const titleLines = tooltip.title || [];
    const bodyLines = tooltip.body.map(b => b.lines);

    let innerHtml = '<div class="tooltip-content">';
    
    titleLines.forEach(title => {
      innerHtml += `<p class="tooltip-title">${title}</p>`;
    });

    bodyLines.forEach(body => {
      innerHtml += `<p class="tooltip-body">${body}</p>`;
    });

    innerHtml += '</div>';
    tooltipElement.innerHTML = innerHtml;
  }

  const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

  // Position tooltip above the point
  tooltipElement.style.opacity = '1';
  tooltipElement.style.left = positionX + tooltip.caretX + 'px';
  tooltipElement.style.top = positionY + tooltip.caretY + 'px';
  tooltipElement.style.transform = 'translate(-50%, -100%)';
};

function renderChart() {
  if (!chartCanvas.value || !hasData.value) return;
  if (chartInstance) {
    chartInstance.destroy();
    chartInstance = null;
  }

  const { labels, data, yAxisConfig } = processPatientGrowthData(series.value);

  chartInstance = new Chart(chartCanvas.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: 'Total Patients',
          data,
          borderColor: '#25B8C2',
          backgroundColor: 'transparent',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          cubicInterpolationMode: 'monotone',
          pointRadius: 5,
          pointHoverRadius: 9,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#25B8C2',
          pointBorderWidth: 3,
          pointHoverBackgroundColor: '#25B8C2',
          pointHoverBorderColor: 'rgba(37, 184, 194, 0.30)',
          pointHoverBorderWidth: 3,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'nearest',
        intersect: true
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external: externalTooltipHandler,
          callbacks: {
            title: function(context) {
              return context[0].label;
            },
            label: function(context) {
              return `Total Patients (Practice View): ${context.parsed.y}`;
            },
          },
        }
      },
      scales: {
        x: {
          grid: { 
            display: true,
            color: 'transparent',
            drawTicks: true,
            tickColor: '#666',
          },
          ticks: { 
            color: '#666',
            font: { size: 12, family: 'Inter', weight: '400' },
          },
          border: { color: '#999', width: 1 },
        },
        y: {
          min: yAxisConfig.min,
          max: yAxisConfig.max,
          ticks: {
            stepSize: yAxisConfig.stepSize,
            color: '#666',
            font: { size: 12, family: 'Inter', weight: '300' },
          },
          grid: { 
            tickColor: '#666',
            color: '#E0E0E0',
            drawTicks: true,     
          },
          border: { 
            color: '#999', 
            width: 1,
            dash: [5, 5]
          },
        },
      },
    },
  });
}

watch(series, () => {
  nextTick(renderChart);
}, { deep: true });

onMounted(() => {
  nextTick(renderChart);
});

onUnmounted(() => {
  if (chartInstance) chartInstance.destroy();
});
</script>

<style scoped>
.chart-section {
  background: #f9fbfd;
  border-radius: 10px;
  padding: 16px 24px;
}

.section-title {
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  color: #333;
  margin: 0 0 8px 0;
}

.chart-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 1px 15px 0 rgba(164, 164, 164, 0.25);
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.chart-container {
  position: relative;
  height: 223px;
  width: 100%;
}

.chart-empty {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9CA3AF;
  font-size: 14px;
}

.chart-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}

.legend-dot {
  width: 16px;
  height: 16px;
  background: #25B8C2;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.1px;
  color: #333;
  text-align: center;
  margin: 0;
}

.chart-container .custom-tooltip {
  position: absolute;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.2s ease;
  z-index: 1000;
  background: transparent !important;
}
</style>

<style>
.chart-container .tooltip-content {
  background: #fff;
  border: 1px solid rgba(164, 164, 164, 0.3);
  border-radius: 5px;
  box-shadow: 0px 1px 15px 0px rgba(164, 164, 164, 0.3);
  padding: 6px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
  white-space: nowrap;
}

.chart-container .tooltip-title {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  color: #666;
  margin: 0;
  text-align: center;
}

.chart-container .tooltip-body {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #333;
  margin: 0;
  text-align: center;
}
</style>
